import React, { useState, useEffect } from 'react';
import { EventItem } from '../../types/event';
import { getEvents } from '../../services/eventService';
import EventSummaryCard from '../common/EventSummaryCard';
import EventSkeleton from '../common/EventSkeleton';
import EventsCarouselSection from '../common/EventsCarouselSection';
import PageHeader from '../common/PageHeader';
import { logger } from '../../utils/logger';
import { useAuth } from '../../context/AuthContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarAlt } from '@fortawesome/free-solid-svg-icons';
import { useAnalytics, useScrollTracking } from '../../hooks/useAnalytics';
import '../../styles/EventsPage.css';

const EventsPage: React.FC = () => {
  const { trackPageView, trackCustomEvent } = useAnalytics();
  useScrollTracking(); // Automatic scroll tracking
  const { currentUser } = useAuth();
  const [events, setEvents] = useState<EventItem[]>([]);
  const [filteredEvents, setFilteredEvents] = useState<EventItem[]>([]);
  // Time-based categorization of events
  const [thisWeekEvents, setThisWeekEvents] = useState<EventItem[]>([]);
  const [nextWeekEvents, setNextWeekEvents] = useState<EventItem[]>([]);
  const [futureEvents, setFutureEvents] = useState<EventItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [showFutureOnly, setShowFutureOnly] = useState<boolean>(true);
  const [dateFilter, setDateFilter] = useState<string>(''); // ISO date string
  // Always use carousel view for future events
  const useCarouselView = true;

  useEffect(() => {
    // Track page view
    trackPageView({ 
      page_title: 'Events Page',
      page_location: window.location.href 
    });

    const fetchEvents = async () => {
      try {
        logger.info('🔄 Loading events for events page', { component: 'EventsPage' });
        setLoading(true);
        setError(null);

        const eventsData = await getEvents();
        setEvents(eventsData);
        
        // Track events loaded event
        trackCustomEvent('events_loaded', {
          event_count: eventsData.length,
          page: 'events_page'
        });
        
        logger.success('✅ Events loaded successfully', { 
          component: 'EventsPage', 
          count: eventsData.length
        });
      } catch (error) {
        logger.error('❌ Failed to load events:', error);
        setError('Unable to load events. Please try again later.');
        
        // Track error event
        trackCustomEvent('events_load_error', {
          page: 'events_page',
          error_type: 'fetch_failed'
        });
      } finally {
        setLoading(false);
      }
    };

    fetchEvents();
  }, [trackPageView, trackCustomEvent]);

  // Track page view
  useEffect(() => {
    trackPageView({
      page_title: 'Events Page',
      page_location: window.location.href
    });
  }, [trackPageView]);

  // Apply filters whenever events, showFutureOnly, or dateFilter change
  useEffect(() => {
    if (events.length === 0) return;

    try {
      const now = new Date();
      now.setHours(0, 0, 0, 0); // Set to start of day for fair comparison

      let filtered = [...events];

      // Apply date filter if specified
      if (dateFilter) {
        const filterDate = new Date(dateFilter);
        filterDate.setHours(0, 0, 0, 0); // Set to start of day

        filtered = filtered.filter(event => {
          const eventDate = new Date(event.date);
          eventDate.setHours(0, 0, 0, 0); // Set to start of day
          
          // Log the date comparison for debugging
          const isMatch = eventDate.toDateString() === filterDate.toDateString();
          logger.debug('🔍 Date filter comparison', {
            eventDate: eventDate.toISOString(),
            filterDate: filterDate.toISOString(),
            isMatch: isMatch
          });
          
          return isMatch;
        });
      } 
      // Apply future/past filter
      else if (showFutureOnly) {
        filtered = filtered.filter(event => {
          const eventDate = new Date(event.date);
          eventDate.setHours(0, 0, 0, 0); // Set to start of day
          return eventDate >= now;
        });
        
        // Categorize events by time period
        categorizeEventsByTime(filtered);
      } else {
        filtered = filtered.filter(event => {
          const eventDate = new Date(event.date);
          eventDate.setHours(0, 0, 0, 0); // Set to start of day
          return eventDate < now;
        });
      }

      // Sort events by date (future events ascending, past events descending)
      filtered.sort((a, b) => {
        const dateA = new Date(a.date).getTime();
        const dateB = new Date(b.date).getTime();
        return showFutureOnly ? dateA - dateB : dateB - dateA;
      });

      setFilteredEvents(filtered);
      logger.info('🔍 Events filtered', { 
        count: filtered.length,        
        filter: showFutureOnly ? 'future' : 'past',
        dateFilter: dateFilter || 'none',
        view: useCarouselView ? 'carousel' : 'grid'
      });
    } catch (error) {
      logger.error('❌ Error filtering events:', error);
      setFilteredEvents([]);
    }
  }, [events, showFutureOnly, dateFilter, useCarouselView]);
    // Categorize events by time period (This Week, Next Week, Future)
  const categorizeEventsByTime = (events: EventItem[]) => {
    const now = new Date();
    const today = new Date(now.setHours(0, 0, 0, 0));
    
    // Calculate the end of the current week (Sunday)
    const endOfWeek = new Date(today);
    const daysToSunday = 7 - today.getDay();
    endOfWeek.setDate(today.getDate() + daysToSunday);
    
    // Calculate the start and end of next week
    const startOfNextWeek = new Date(endOfWeek);
    startOfNextWeek.setDate(startOfNextWeek.getDate() + 1); // Monday
    
    const endOfNextWeek = new Date(startOfNextWeek);
    endOfNextWeek.setDate(startOfNextWeek.getDate() + 6); // Sunday
    
    // Start of the period after next week
    const startOfFuture = new Date(endOfNextWeek);
    startOfFuture.setDate(startOfFuture.getDate() + 1);
    
    logger.info('📅 Time ranges calculated', {
      today: today.toISOString().split('T')[0],
      endOfWeek: endOfWeek.toISOString().split('T')[0],
      startOfNextWeek: startOfNextWeek.toISOString().split('T')[0],
      endOfNextWeek: endOfNextWeek.toISOString().split('T')[0],
      startOfFuture: startOfFuture.toISOString().split('T')[0]
    });
    
    // Filter events by date ranges
    const thisWeek = events.filter(event => {
      const eventDate = new Date(event.date);
      eventDate.setHours(0, 0, 0, 0); // Normalize to start of day for fair comparison
      
      // Log the comparison for debugging
      logger.debug('📅 Comparing event date with today', { 
        eventDate: eventDate.toISOString(),
        today: today.toISOString(),
        endOfWeek: endOfWeek.toISOString(),
        isThisWeek: eventDate >= today && eventDate <= endOfWeek
      });
      
      return eventDate >= today && eventDate <= endOfWeek;
    });
    
    const nextWeek = events.filter(event => {
      const eventDate = new Date(event.date);
      eventDate.setHours(0, 0, 0, 0); // Normalize to start of day
      return eventDate >= startOfNextWeek && eventDate <= endOfNextWeek;
    });
    
    const future = events.filter(event => {
      const eventDate = new Date(event.date);
      eventDate.setHours(0, 0, 0, 0); // Normalize to start of day
      return eventDate >= startOfFuture;
    });
    
    setThisWeekEvents(thisWeek);
    setNextWeekEvents(nextWeek);
    setFutureEvents(future);
    
    logger.info('📊 Events categorized by time period', {
      thisWeekCount: thisWeek.length,
      nextWeekCount: nextWeek.length,
      futureCount: future.length,
      today: today.toISOString()
    });
  };

  // Format the date for display
  const formatEventDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Format the time for display
  const formatEventTime = (event: EventItem): string => {
    return `${event.startTime.doorsOpen} - ${event.endTime || 'TBD'}`;
  };

  // Get the theme from description
  const getEventTheme = (description: string): string => {
    if (description.toLowerCase().includes('play-party')) return 'Play-Party';
    if (description.toLowerCase().includes('workshop')) return 'Workshop';
    if (description.toLowerCase().includes('social')) return 'Social';
    return 'Event';
  };

  // Handle date filter change
  const handleDateFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDateFilter(e.target.value);
    trackCustomEvent('date_filter_applied', { 
      filter_date: e.target.value,
      page: 'events_page'
    });
  };

  // Clear the date filter
  const clearDateFilter = () => {
    setDateFilter('');
    trackCustomEvent('date_filter_cleared', {
      page: 'events_page'
    });
  };

  return (
    <>
      <PageHeader
        title="Events"
        subtitle="Discover our upcoming and past events"
      />

      <section className="container evp-events-container">
        <div className="evp-filters-container">
          <div className="evp-toggle-container">
            <button 
              className={`evp-toggle-button ${showFutureOnly ? 'active' : ''}`}
              onClick={() => {
                setShowFutureOnly(true);
                trackCustomEvent('Filter Toggled', { filter: 'Upcoming Events' });
              }}
            >
              Upcoming Events
            </button>
            <button 
              className={`evp-toggle-button ${!showFutureOnly ? 'active' : ''}`}
              onClick={() => {
                setShowFutureOnly(false);
                trackCustomEvent('Filter Toggled', { filter: 'Past Events' });
              }}
            >
              Past Events
            </button>
          </div>
          
          <div className="evp-date-filter">
            <div className="evp-date-input-container">
              <label htmlFor="dateFilter">
                <FontAwesomeIcon icon={faCalendarAlt} /> Filter by date:
              </label>
              <input 
                type="date" 
                id="dateFilter"
                value={dateFilter}
                onChange={handleDateFilterChange}
                className="evp-date-input"
              />
              {dateFilter && (
                <button 
                  className="evp-clear-filter"
                  onClick={clearDateFilter}
                  aria-label="Clear date filter"
                >
                  ×
                </button>
              )}
            </div>
          </div>
          
          {/* View toggle buttons removed as per requirement */}
        </div>

        {loading && (
          <div className="evp-loading">
            <EventSkeleton count={6} />
          </div>
        )}

        {error && (
          <div className="evp-error">
            <p>{error}</p>
          </div>
        )}

        {!loading && !error && showFutureOnly && !dateFilter && useCarouselView && (
          <>
            <EventsCarouselSection 
              title="THIS WEEK" 
              events={thisWeekEvents} 
              emptyMessage="No events this week. Check back soon!"
              currentUser={currentUser}
            />
            
            <EventsCarouselSection 
              title="NEXT WEEK" 
              events={nextWeekEvents} 
              emptyMessage="No events scheduled for next week yet."
              currentUser={currentUser}
            />
            
            <EventsCarouselSection 
              title="FUTURE EVENTS" 
              events={futureEvents} 
              emptyMessage="No events scheduled beyond the next two weeks."
              currentUser={currentUser}
            />
            
            {thisWeekEvents.length === 0 && nextWeekEvents.length === 0 && futureEvents.length === 0 && (
              <div className="evp-no-events">
                <p>No upcoming events at the moment. Check back soon!</p>
              </div>
            )}
          </>
        )}

        {!loading && !error && (!showFutureOnly || dateFilter || !useCarouselView) && filteredEvents.length === 0 && (
          <div className="evp-no-events">
            <p>
              {dateFilter 
                ? `No events found for ${new Date(dateFilter).toLocaleDateString('en-US', { 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric' 
                  })}.`
                : showFutureOnly
                  ? 'No upcoming events at the moment. Check back soon!'
                  : 'No past events to display.'
              }
            </p>
          </div>
        )}

        {!loading && !error && (!showFutureOnly || dateFilter || !useCarouselView) && filteredEvents.length > 0 && (
          <div className="evp-event-grid">
            {filteredEvents.map(event => (
              <EventSummaryCard
                key={event.id}
                title={event.title}
                description={event.description}
                imageSrc={event.imageUrl || '/images/room_events.jpg'} // Fallback image
                theme={getEventTheme(event.description)}
                linkHref={`/events/${event.id}`} // Link to individual event page
                linkText={`Details for ${formatEventDate(event.date)}`}
                date={formatEventDate(event.date)}
                time={formatEventTime(event)}
                location={event.address}
                eventId={event.id}
                currentUser={currentUser}
              />
            ))}
          </div>
        )}
      </section>
    </>
  );
};

export default EventsPage;